"use client";

import * as React from "react";
import { Label as LabelPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";

/* The Sirdab label: the third element of every form row. 14px medium in
   foreground (explicit, so it holds on tinted surfaces), normal role
   leading, never selectable. The field recipe: label → field → helper/error,
   6px stack gap, always wired with htmlFor.

   Flex + 6px gap supports the inline form: a control beside its text
   (checkbox / radio / switch rows). The disabled hooks are positional:
   peer-disabled needs the label AFTER its field; group-data-disabled needs
   a group wrapper. In the stacked recipe (label first) neither fires, and
   the field dims alone, which is the intended behaviour.

   display: flex makes the label block-level, so the whole row (including
   the space after the text) is clickable and focuses the field. DECISION:
   kept for the bigger hit target; inline-flex would hug the text. A row
   that wraps a control (checkbox / radio / switch) shows cursor-pointer
   across the whole row via has-[button], and not-allowed when that control
   is disabled; stacked form labels keep the default cursor.

   Deliberately NOT variants (rejected as drift): cursor-pointer and
   gray-900 text on auth forms, an 8px gap for the wrapping-label case,
   and labels rendered as bare <p> tags. Ours stays a real <label>.

   REQUIRED-FIELD CONVENTION [decision]: mark the MINORITY, never with an
   asterisk (a status colour by the back door). In most forms required is
   the majority, so it goes unmarked and optional fields carry the
   localized "(اختياري)" / "(optional)" via `hint`. In a majority-optional
   form (settings, long editors) flip it: mark the required ones, as
   text, still never an asterisk. The caller passes the string either way;
   the primitive stays i18n-free.

   hint joins the ACCESSIBLE NAME: everything inside <label> is announced
   as part of the field's name. "Weight, optional" and "Weight, kg" read
   right; a sentence of context does not. Keep hint to a word or two
   (optional/required marker, units); anything longer is helper text below
   the field, wired with aria-describedby, not a second hint.

   Usage:
       <Label htmlFor="name">Product name</Label>
       <Label htmlFor="bio" hint={t("optional")}>Bio</Label>
       <Label htmlFor="live"><Switch id="live" /> Store is live</Label>
*/
function Label({
  className,
  hint,
  children,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root> & {
  hint?: React.ReactNode;
}) {
  return (
    <LabelPrimitive.Root
      data-slot="label"
      className={cn(
        "text-body flex items-center gap-1.5 text-foreground select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 has-[button]:cursor-pointer has-[button:disabled]:cursor-not-allowed",
        className,
      )}
      {...props}
    >
      {children}
      {hint != null ? (
        <span className="text-caption-quiet text-muted-foreground">{hint}</span>
      ) : null}
    </LabelPrimitive.Root>
  );
}

export { Label };
